// AppointmentTypeManager.js - Admin för bokningstyper

import React, { useState, useEffect } from 'react';
import {
  getAppointmentTypes,
  createAppointmentType,
  updateAppointmentType,
  deleteAppointmentType,
  getBookingConfirmationOptions
} from './calendarApi';
import './AppointmentTypeManager.css';

// Mjuk, dämpad standardpalett (pastellton, jämn ljushet) så bokningstyper
// får lugna färger som standard istället för hårda, mättade toner.
const DEFAULT_COLORS = [
  '#8CB4E0', // dimmig blå
  '#9FD0A6', // salviagrön
  '#E8A6A6', // dammig korall
  '#F2CF95', // mjuk amber
  '#9AD0D0', // pudrig teal
  '#BCA9DE', // ljuslila
  '#EFB0CE', // dammrosa
  '#F0BE99', // mjuk aprikos
  '#A6DAC5', // mynta
  '#BAC2CB'  // varmgrå
];

// Förvald färg för nya bokningstyper (första i den mjuka paletten).
const DEFAULT_COLOR = DEFAULT_COLORS[0];

// Standardbekräftelse per typ (Spår B0): 'inherit' = följ klinikens förval
const CONFIRMATION_DEFAULT_LABELS = {
  inherit: 'Klinikens förval',
  send: 'Ja',
  none: 'Nej'
};

const AppointmentTypeManager = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [confirmationTemplates, setConfirmationTemplates] = useState({ sms: [], email: [] });

  const [formData, setFormData] = useState({
    name: '',
    duration_minutes: 30,
    color: DEFAULT_COLOR,
    description: '',
    default_price: 0,
    online_bookable: false,
    confirmation_default: 'inherit',
    confirmation_sms_template_id: '',
    confirmation_email_template_id: ''
  });

  // Ladda bokningstyper
  const loadTypes = async () => {
    try {
      setLoading(true);
      const data = await getAppointmentTypes();
      setTypes(data);
    } catch (err) {
      setError('Kunde inte ladda bokningstyper');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTypes();
    // Valbara bekräftelsemallar - fel här ska inte stoppa typhanteringen
    getBookingConfirmationOptions()
      .then(data => setConfirmationTemplates({
        sms: data.sms_templates || [],
        email: data.email_templates || []
      }))
      .catch(err => console.error('Kunde inte ladda bekräftelsemallar', err));
  }, []);

  // Starta redigering
  const handleEdit = (type) => {
    setEditingId(type.type_id);
    setFormData({
      name: type.name,
      duration_minutes: type.duration_minutes,
      color: type.color,
      description: type.description || '',
      default_price: type.default_price || 0,
      online_bookable: type.online_bookable || false,
      confirmation_default: type.confirmation_default || 'inherit',
      confirmation_sms_template_id: type.confirmation_sms_template_id || '',
      confirmation_email_template_id: type.confirmation_email_template_id || ''
    });
  };

  // Avbryt redigering
  const handleCancel = () => {
    setEditingId(null);
    setFormData({
      name: '',
      duration_minutes: 30,
      color: DEFAULT_COLOR,
      description: '',
      default_price: 0,
      online_bookable: false,
      confirmation_default: 'inherit',
      confirmation_sms_template_id: '',
      confirmation_email_template_id: ''
    });
  };

  // Spara (skapa eller uppdatera)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Namn krävs');
      return;
    }

    // Tom mall = klinikens mall (null till API:t)
    const payload = {
      ...formData,
      confirmation_sms_template_id: formData.confirmation_sms_template_id || null,
      confirmation_email_template_id: formData.confirmation_email_template_id || null
    };

    try {
      setError(null);
      if (editingId) {
        await updateAppointmentType(editingId, payload);
      } else {
        await createAppointmentType(payload);
      }
      handleCancel();
      loadTypes();
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte spara bokningstyp');
    }
  };

  // Ta bort
  const handleDelete = async (typeId) => {
    if (!window.confirm('Är du säker på att du vill inaktivera denna bokningstyp?')) {
      return;
    }

    try {
      await deleteAppointmentType(typeId);
      loadTypes();
    } catch (err) {
      setError('Kunde inte ta bort bokningstyp');
    }
  };

  if (loading && types.length === 0) {
    return <div className="atm-loading">Laddar...</div>;
  }

  return (
    <div className="appointment-type-manager">
      <div className="atm-header">
        <h2>Bokningstyper</h2>
        <p className="atm-description">
          Hantera olika typer av bokningar med varaktighet och färgkodning.
        </p>
      </div>

      {error && <div className="atm-error">{error}</div>}

      {/* Formulär för ny/redigera */}
      <div className="atm-form-container">
        <h3>{editingId ? 'Redigera bokningstyp' : 'Ny bokningstyp'}</h3>
        <form onSubmit={handleSubmit} className="atm-form">
          <div className="atm-form-row">
            <div className="atm-form-group">
              <label>Namn *</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="T.ex. Nybesök, Återbesök, Telefonkonsultation"
              />
            </div>

            <div className="atm-form-group atm-form-group-small">
              <label>Varaktighet (min)</label>
              <input
                type="number"
                min="5"
                max="480"
                step="5"
                value={formData.duration_minutes}
                onChange={e => setFormData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 30 }))}
              />
            </div>

            <div className="atm-form-group atm-form-group-small">
              <label>Standardpris (kr)</label>
              <input
                type="number"
                min="0"
                step="50"
                value={formData.default_price}
                onChange={e => setFormData(prev => ({ ...prev, default_price: parseFloat(e.target.value) || 0 }))}
                placeholder="0"
              />
            </div>

            <div className="atm-form-group atm-form-group-small">
              <label>Färg</label>
              <div className="atm-color-picker">
                <input
                  type="color"
                  value={formData.color}
                  onChange={e => setFormData(prev => ({ ...prev, color: e.target.value }))}
                />
                <div className="atm-color-presets">
                  {DEFAULT_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      className={`atm-color-preset ${formData.color === color ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFormData(prev => ({ ...prev, color }))}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="atm-form-group">
            <label>Beskrivning</label>
            <input
              type="text"
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Valfri beskrivning..."
            />
          </div>

          <div className="atm-form-group atm-form-group-checkbox">
            <label className="atm-checkbox-label">
              <input
                type="checkbox"
                checked={formData.online_bookable}
                onChange={e => setFormData(prev => ({ ...prev, online_bookable: e.target.checked }))}
              />
              <span className="atm-checkbox-text">
                <i className="fas fa-globe"></i>
                Tillgänglig för onlinebokning
              </span>
              <span className="atm-checkbox-hint">Visas i Aion Bookings</span>
            </label>
          </div>

          <fieldset className="atm-confirmation">
            <legend>Bokningsbekräftelse</legend>
            <p className="atm-checkbox-hint">
              Fylls i automatiskt i bokningsrutan när typen väljs och kan ändras per bokning.
              Gäller även onlinebokning. Påverkar inte påminnelsen före besöket.
            </p>
            <div className="atm-form-row">
              <div className="atm-form-group atm-form-group-small">
                <label>Skicka som standard</label>
                <select
                  value={formData.confirmation_default}
                  onChange={e => setFormData(prev => ({ ...prev, confirmation_default: e.target.value }))}
                >
                  {Object.entries(CONFIRMATION_DEFAULT_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="atm-form-group">
                <label>SMS-mall</label>
                <select
                  value={formData.confirmation_sms_template_id}
                  onChange={e => setFormData(prev => ({ ...prev, confirmation_sms_template_id: e.target.value }))}
                >
                  <option value="">Klinikens SMS-mall</option>
                  {confirmationTemplates.sms.map(t => (
                    <option key={t.template_id} value={t.template_id}>{t.template_name}</option>
                  ))}
                </select>
              </div>
              <div className="atm-form-group">
                <label>E-postmall</label>
                <select
                  value={formData.confirmation_email_template_id}
                  onChange={e => setFormData(prev => ({ ...prev, confirmation_email_template_id: e.target.value }))}
                >
                  <option value="">Klinikens e-postmall</option>
                  {confirmationTemplates.email.map(t => (
                    <option key={t.template_id} value={t.template_id}>{t.template_name}</option>
                  ))}
                </select>
              </div>
            </div>
          </fieldset>

          <div className="atm-form-actions">
            <button type="submit" className="btn btn-primary">
              {editingId ? 'Uppdatera' : 'Skapa'}
            </button>
            {editingId && (
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Avbryt
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Lista över bokningstyper */}
      <div className="atm-list">
        <h3>Befintliga bokningstyper</h3>
        {types.length === 0 ? (
          <p className="atm-empty">Inga bokningstyper skapade än. Skapa din första ovan!</p>
        ) : (
          <div className="atm-grid">
            {types.map(type => (
              <div key={type.type_id} className="atm-card">
                <div className="atm-card-color" style={{ backgroundColor: type.color }} />
                <div className="atm-card-content">
                  <div className="atm-card-header">
                    <h4>{type.name}</h4>
                    <div className="atm-card-meta">
                      <span className="atm-duration">{type.duration_minutes} min</span>
                      {type.default_price > 0 && (
                        <span className="atm-price">{type.default_price} kr</span>
                      )}
                      {type.online_bookable && (
                        <span className="atm-online-badge" title="Tillgänglig för onlinebokning">
                          <i className="fas fa-globe"></i>
                        </span>
                      )}
                    </div>
                  </div>
                  {type.description && (
                    <p className="atm-card-description">{type.description}</p>
                  )}
                  {(type.confirmation_default !== 'inherit' || type.confirmation_sms_template_name
                    || type.confirmation_email_template_name) && (
                    <p className="atm-card-confirmation">
                      <i className="fas fa-check-circle"></i>{' '}
                      Bekräftelse: {CONFIRMATION_DEFAULT_LABELS[type.confirmation_default] || 'Klinikens förval'}
                      {type.confirmation_sms_template_name && ` · SMS: ${type.confirmation_sms_template_name}`}
                      {type.confirmation_email_template_name && ` · E-post: ${type.confirmation_email_template_name}`}
                    </p>
                  )}
                  <div className="atm-card-actions">
                    <button
                      className="btn-icon"
                      onClick={() => handleEdit(type)}
                      title="Redigera"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="btn-icon btn-icon-danger"
                      onClick={() => handleDelete(type.type_id)}
                      title="Ta bort"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentTypeManager;
